/* @flow */

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import Box from '@material-ui/core/Box';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import TextField from '@material-ui/core/TextField';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';

import ButtonComponent from 'components/buttons/button';
import FormErrorsComponent from 'components/formErrors';
import IntlMessageComponent from 'components/formatters/intlMessage';
import PasswordRulesComponent from 'components/passwordRules';
import PageSubTitleViewComponent from 'components/pageTitle/subTitle';
import TwoFactorSetupComponent from 'app/users/twoFactorSetup';

import ResetPasswordForm from './model';

import { PROTECTED_APP } from 'constants/pageRoutes';

type ChangePasswordViewComponentProps = {
  errors: Map<string,?string>;
  form: ResetPasswordForm;
  formErrors: string[];
  isProcessing: boolean;
  twoFactorMode: string;
  hasMethod: boolean;
  methodLabelId: string;
  isImpersonating: boolean;
  resetDialogOpen: boolean;
  isProcessingReset: boolean;
  handleCurrentPasswordChange: (value: string) => void;
  handlePasswordChange: (value: string) => void;
  handleConfirmPasswordChange: (value: string) => void;
  submitForm: () => void;
  startTwoFactorSetup: () => void;
  cancelTwoFactorSetup: () => void;
  completeTwoFactorSetup: () => void;
  openResetDialog: () => void;
  closeResetDialog: () => void;
  confirmReset: () => void;
}

export default function ChangePasswordViewComponent(props: ChangePasswordViewComponentProps) {
  const onCurrentPasswordChange = (event) => props.handleCurrentPasswordChange(event.target.value);
  const onPasswordChange = (event) => props.handlePasswordChange(event.target.value);
  const onConfirmPasswordChange = (event) => props.handleConfirmPasswordChange(event.target.value);

  return (
    <React.Fragment>
      <Paper elevation={0}>
        <Box p={5}>
          <PageSubTitleViewComponent id="form.password.header" />
          <FormErrorsComponent formErrors={props.formErrors} />

          <Box mt={3}></Box>

          <PasswordRulesComponent />

          <Box mt={3}>
            <Grid container direction="row" justify="flex-start" alignItems="flex-start" spacing={1}>
              <Grid item xs={12} md={6}>
                <TextField
                  id="form.currentPassword"
                  label={<IntlMessageComponent id="form.currentpassword.label" />}
                  error={!!props.errors.get('currentPassword')}
                  helperText={(props.errors.get('currentPassword')) ?
                    <IntlMessageComponent id={props.errors.get('currentPassword')} /> : ''}
                  value={props.form.currentPassword}
                  onChange={onCurrentPasswordChange}
                  fullWidth
                  margin="normal"
                  required
                  type="password"
                />

                <TextField
                  id="form.password"
                  data-walkme="password-field-newpassword"
                  label={<IntlMessageComponent id="form.password.label" />}
                  error={!!props.errors.get('password')}
                  helperText={(props.errors.get('password')) ?
                    <IntlMessageComponent id={props.errors.get('password')} /> : ''}
                  value={props.form.password}
                  onChange={onPasswordChange}
                  fullWidth
                  margin="normal"
                  required
                  type="password"
                />

                <TextField
                  id="form.confirmpassword"
                  label={<IntlMessageComponent id="form.confirmpassword.label" />}
                  error={!!props.errors.get('confirmPassword')}
                  helperText={(props.errors.get('confirmPassword')) ?
                    <IntlMessageComponent id={props.errors.get('confirmPassword')} /> : ''}
                  value={props.form.confirmPassword}
                  onChange={onConfirmPasswordChange}
                  fullWidth
                  margin="normal"
                  required
                  type="password"
                />
              </Grid>
            </Grid>
          </Box>

          <Box mt={5}>
            <ButtonComponent
              color="default"
              variant="contained"
              component={RouterLink}
              to={PROTECTED_APP}
            ><IntlMessageComponent id="general.cancel" /></ButtonComponent>

            <Box component="span" ml={1}>
              <ButtonComponent
                color="primary"
                variant="contained"
                isProcessing={props.isProcessing}
                onClick={props.submitForm}
              ><IntlMessageComponent id="general.submit" /></ButtonComponent>
            </Box>
          </Box>
        </Box>
      </Paper>
      <Box p={3}></Box>
      <Paper elevation={0}>
        <Box p={5}>
          <PageSubTitleViewComponent id="twofactor.header" />

          {props.twoFactorMode === 'setup' ? (
            <Box mt={3}>
              <TwoFactorSetupComponent
                onComplete={props.completeTwoFactorSetup}
                onCancel={props.cancelTwoFactorSetup}
                cancelLabelId="general.cancel"
              />
            </Box>
          ) : (
            <React.Fragment>
              <Box mt={3}>
                <Grid container direction="row" alignItems="center" spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="textSecondary">
                      <IntlMessageComponent id="twofactor.method.label" />
                    </Typography>
                    <Box borderBottom={1} borderColor="grey.400" pb={1}>
                      <Typography variant="body1">
                        {props.methodLabelId
                          ? <IntlMessageComponent id={props.methodLabelId} />
                          : ' '}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <ButtonComponent
                      color="primary"
                      variant="contained"
                      disabled={props.isImpersonating}
                      onClick={props.startTwoFactorSetup}
                    ><IntlMessageComponent id={props.hasMethod ? 'twofactor.changemethod' : 'twofactor.setmethod'} /></ButtonComponent>
                  </Grid>
                </Grid>
              </Box>

              {props.hasMethod &&
                <Box mt={2}>
                  <Link
                    component="button"
                    type="button"
                    onClick={props.isImpersonating ? undefined : props.openResetDialog}
                    style={props.isImpersonating ? { pointerEvents: 'none', color: 'grey' } : undefined}
                  ><IntlMessageComponent id="twofactor.reset.link" /></Link>
                </Box>
              }

              <Dialog open={props.resetDialogOpen} onClose={props.closeResetDialog} fullWidth maxWidth="sm">
                <DialogTitle><IntlMessageComponent id="twofactor.reset.title" /></DialogTitle>
                <DialogContent>
                  <DialogContentText><IntlMessageComponent id="twofactor.reset.text" /></DialogContentText>
                </DialogContent>
                <DialogActions>
                  <ButtonComponent
                    color="default"
                    variant="contained"
                    onClick={props.closeResetDialog}
                  ><IntlMessageComponent id="general.cancel" /></ButtonComponent>
                  <ButtonComponent
                    color="primary"
                    variant="contained"
                    isProcessing={props.isProcessingReset}
                    onClick={props.confirmReset}
                  ><IntlMessageComponent id="twofactor.reset.confirm" /></ButtonComponent>
                </DialogActions>
              </Dialog>
            </React.Fragment>
          )}
        </Box>
      </Paper>
    </React.Fragment>
  );
}
